import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormBuilder, NG_VALIDATORS, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import {
  AlertController,
  LoadingController,
  ModalController,
  NavParams,
} from "ionic-angular";
import moment from "moment";
import { from } from "rxjs/observable/from";
import { of } from "rxjs/observable/of";
import { catchError, map, mergeMap, toArray } from "rxjs/operators";
import { Config } from "../../../pages/patient-chart/patient-benefits/models";
import { PayersService } from "../../../providers/payers-service/payers.service";
import { UtilityService } from "../../../providers/utility-service/utility-service";
import { NgbCustomParserFormatter } from "../../common/date-picker/ngb-custom-parser-formatter";
import { ContractPeriod } from "../rates-payer/models";

@Component({
  selector: "billing-options",
  templateUrl: "billing-options.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BillingOptions),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BillingOptions),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
})
export class BillingOptions implements OnInit, OnDestroy {
  selectedBenefitType: string = "HOSPICE";
  saveEnabled: boolean = false;
  claimFormTypeList: Config[] = [
    { name: "Professional", value: "PROFESSIONAL", disabled: false },
    { name: "Institutional", value: "INSTITUTIONAL", disabled: false },
  ];
  billByList: Config[] = [
    { name: "LOC/Service", value: "LOC/Service", disabled: false },
    { name: "Day", value: "Day", disabled: false },
  ];
  payerBillingOptions: any = null;
  dateFormat: string = "YYYY-MM-DD";
  dateUIFormat: string = "MM/DD/YYYY";
  contractPeriodsOptions: ContractPeriod[];
  currentContractPeriod: any = null;
  previousContractPeriod: any;
  showSection: boolean = false;
  claimFormType: any;
  billBy: any;
  tenantId: any;
  payerId: any;
  payerType: any;

  @Input() isEditMode: boolean = false;
  @Input() currentPayer: any;
  @Input() currentPayerType: any;
  @Input() currentBenefitType: any;
  @Output() onCanceled = new EventEmitter<any>();
  @Output() onSetCheckmark = new EventEmitter<any>();

  constructor(
    protected formBuilder: FormBuilder,
    protected alertController: AlertController,
    protected navParams: NavParams,
    private payersService: PayersService,
    private utilityService: UtilityService,
    public modalCtrl: ModalController,
    private loadingCtrl: LoadingController
  ) {}
  ngOnDestroy() {}

  emptyContractPeriod() {
    return null;
  }

  ngOnChanges() {
    this.contractPeriodsOptions = [];
    if (
      this.currentPayer &&
      this.currentPayer.payerId &&
      this.currentPayer.payerId.length > 0 &&
      this.currentPayer.id != 0
    ) {
      this.tenantId = this.currentPayer.tenantId;
      this.payerId = this.currentPayer.payerId;
      this.payerType = this.currentPayer.payerType;
      this.getPayerBillingOptionsPeriods();
    } else {
      this.loadOptions();
    }
  }

  loadOptions(): void {
    let addNewContractPeriodOption: ContractPeriod = {
      id: 0,
      name: "Add New",
      value: "Add New",
      effectiveDate: "",
      expiredDate: "",
    };
    this.contractPeriodsOptions = [
      addNewContractPeriodOption,
      ...this.contractPeriodsOptions,
    ];
  }

  ngOnInit(): void {
    if (
      this.currentPayer &&
      this.currentBenefitType &&
      this.currentBenefitType.toUpperCase().includes("HOSPICE")
    )
      this.selectedBenefitType = "HOSPICE";
    else if (
      this.currentPayer &&
      this.currentBenefitType &&
      this.currentBenefitType
        .toUpperCase()
        .replaceAll(" ", "")
        .includes("ROOMANDBOARD")
    ) {
      this.selectedBenefitType = "ROOMANDBOARD";
    }

    if (
      this.currentPayer &&
      this.currentPayer.payerId &&
      this.currentPayer.payerId.length > 0 &&
      this.currentPayer.id != 0
    ) {
      this.tenantId = this.currentPayer.tenantId;
      this.payerId = this.currentPayer.payerId;
      this.payerType = this.currentPayer.payerType;
    }

    this.saveEnabled = true;
    this.initForm();
    this.getPayerBillingOptionsPeriods();
    if (this.currentBenefitType)
      this.validateCheckmarkInfo().then((result) => {
        if (result) this.onSetCheckmark.emit();
      });
  }

  onPeriodChanged() {
    this.showSection = false;
    this.currentContractPeriod = null;
    this.clearContractPeriodBillingOptions();
    this.getPayerBillingOptionsPeriods();
  }

  initForm() {}

  private validateCheckmarkInfo() {
    let benefitTypes = this.currentBenefitType.split(",");

    return new Promise((resolve, reject) => {
      const apiCalls = [];
      for (let benefit of benefitTypes) {
        if (benefit === "HOSPICE") {
          apiCalls.push(
            this.payersService.getPayerBillingOptionsPeriods(
              this.currentPayer.payerId,
              "HOSPICE",
              "BILLINGOPTIONS"
            )
          );
        } else if (
          benefit.toUpperCase().replaceAll(" ", "") === "ROOMANDBOARD"
        ) {
          apiCalls.push(
            this.payersService.getPayerBillingOptionsPeriods(
              this.currentPayer.payerId,
              "ROOMANDBOARD",
              "BILLINGOPTIONS"
            )
          );
        }
      }
      from(apiCalls)
        .pipe(
          mergeMap((apiCall, index) => {
            return apiCall.pipe(
              catchError((error) => {
                return of(null);
              })
            );
          }),
          toArray(),
          map((results) => {
            // @ts-ignore
            const hasValidResult = results.some(
              (result) =>
                // @ts-ignore
                result !== null && result.length > 0
            );

            resolve(hasValidResult);
            return hasValidResult;
          })
        )
        .subscribe({
          error: (error) => reject(error),
          complete: () => {},
        });
    });
  }

  checkTabEnabled(benefitType: string) {
    return (
      this.currentPayer &&
      this.currentPayer.benefitType &&
      this.currentPayer.benefitType
        .replace(/ /g, "")
        .toUpperCase()
        .indexOf(benefitType) >= 0
    );
  }

  changeBenefitType(benefitType: string) {
    this.selectedBenefitType = benefitType;
    this.currentContractPeriod = null;
    this.clearContractPeriodBillingOptions();
    this.showSection = false;
    this.getPayerBillingOptionsPeriods();
  }

  cancel(): void {
    this.onCanceled.emit(true);
  }

  private onSaveError = (error) => {
    this.saveEnabled = true;
    this.utilityService.decreaseSpinnerCount();
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: "Error while saving Billing Options " + error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: "Error while saving Billing Options " + error.message,
        });
      }
    }
  };

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
        },
      ],
    });
    alert.present();
  };

  showSaveModal() {
    let modalPage = this.modalCtrl.create(
      "SaveFacilityPage",
      {
        title: "Payer Information Change",
        commentPlaceholderKey: "archiveFacilityCommentPlaceHolder",
        popupMessageKey: "savePayerWarningMessage",
        section: "payer",
      },
      {
        enableBackdropDismiss: false,
        cssClass: "save-payer-confirmation-popup",
      }
    );
    modalPage.onDidDismiss((data) => {
      if (data.save) {
        let body = this.createBody();
        this.utilityService.increaseSpinnerCount("Loading");
        this.payersService.postPayerBillingOptions(body).subscribe((datas) => {
          setTimeout(() => {
            this.utilityService.decreaseSpinnerCount();
            this.onSetCheckmark.emit();
            this.saveEnabled = false;
            this.clearContractPeriodBillingOptions();
            this.currentContractPeriod = null;
            this.getPayerBillingOptionsPeriods();
            this.showAlert({
              title: "Success",
              message: "Billing options saved.",
            });
          }, 1000);
        }, this.onSaveError);
      }
    });
    modalPage.present();
  }

  createBody() {
    const body = {
      start: moment(new Date(this.getEffectiveDate())).format(this.dateFormat),
      benefitType: this.selectedBenefitType,
      claimFormType: this.claimFormType,
      billGrouping: this.billBy,
      payerId: this.currentPayer.payerId,
    };

    if (this.getExpiredDate() != "") {
      body["end"] = moment(new Date(this.getExpiredDate())).format(
        this.dateFormat
      );
    }

    return body;
  }

  validateOptions() {
    return !(
      this.claimFormType == null ||
      this.claimFormType == "" ||
      this.billBy == null ||
      this.billBy == ""
    );
  }

  async onContractPeriodChange(event: any) {
    if (!event) {
      this.currentContractPeriod = this.emptyContractPeriod();
      this.saveEnabled = false;
      this.previousContractPeriod = this.currentContractPeriod;
      this.clearContractPeriodBillingOptions();
      this.showSection = false;
      return;
    }
    if (event.value == "Add New") {
      this.addNewContractPeriod();
    } else {
      this.currentContractPeriod = {
        id: event.id,
        name: event.name,
        value: event.value,
        expiredDate: event.expiredDate,
        effectiveDate: event.effectiveDate,
      };
      this.getPayerBillingOptionsByPeriod(
        this.selectedBenefitType,
        this.currentContractPeriod.effectiveDate
      );
      this.previousContractPeriod = this.currentContractPeriod;
      this.saveEnabled = true;
    }
  }

  onContractPeriodClear(event: any) {
    this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
      (option) => option.id != 0
    );
    this.loadOptions();
  }

  addNewContractPeriod() {
    let minDate: moment.Moment = moment("2023-09-30", this.dateFormat);
    this.contractPeriodsOptions.forEach((contractPeriod) => {
      let periodStart = moment(contractPeriod.effectiveDate);
      if (periodStart.isAfter(minDate)) {
        minDate = periodStart;
      }
    });

    let contractPeriodModal = this.modalCtrl.create(
      "ContractPeriodModalComponent",
      { minDateFormat: minDate },
      { cssClass: "contract-period-modal" }
    );

    contractPeriodModal.onDidDismiss((data) => {
      if (data) {
        let newOption = this.formatContractPeriodModel(data);
        let contractPeriods = this.contractPeriodsOptions.filter(
          (contract) => contract.value != "Add New"
        );
        if (
          contractPeriods.length === 0 ||
          newOption.effectiveDate >
            this.getMostRecentContractPeriod(contractPeriods).effectiveDate
        ) {
          this.saveNewContractPeriodOption(newOption);
        } else {
          this.showAlert({
            title: "Error",
            message:
              "Contract period’s effective date can’t be before or equal to the active period’s date.",
          });
          this.onNewPeriodCanceled();
        }
      } else {
        this.onNewPeriodCanceled();
      }
    });
    contractPeriodModal.present();
  }

  onNewPeriodCanceled() {
    this.currentContractPeriod =
      this.previousContractPeriod &&
      this.previousContractPeriod.value != "Add New"
        ? this.previousContractPeriod
        : this.emptyContractPeriod();
  }

  saveNewContractPeriodOption(newOption) {
    this.contractPeriodsOptions = [newOption, ...this.contractPeriodsOptions];
    this.currentContractPeriod = newOption;
    this.clearContractPeriodBillingOptions();
    this.removeNewContractPeriodOption();
    this.saveEnabled = true;
    this.showSection = true;
    this.billBy = "LOC/Service";
    this.claimFormType = "INSTITUTIONAL";
  }

  removeNewContractPeriodOption() {
    this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
      (option) => option.value != "Add New"
    );
  }

  formatContractPeriodModel(period: any): ContractPeriod {
    let newContractPeriod = moment(new Date(period)).format(this.dateUIFormat);
    let newEffectiveDate = moment(new Date(period)).format(this.dateFormat);
    return {
      id: 0,
      name: newContractPeriod,
      value: newContractPeriod,
      effectiveDate: newEffectiveDate,
      expiredDate: null,
    };
  }

  getPayerBillingOptionsPeriods() {
    let spinnerText = "Loading Contract Periods";

    const spinner = this.loadingCtrl.create({
      content: spinnerText,
    });
    spinner.present();
    this.payersService
      .getPayerBillingOptionsPeriods(
        this.currentPayer.payerId,
        this.selectedBenefitType,
        "BILLINGOPTIONS"
      )
      .pipe()
      .take(1)
      .subscribe((response) => {
        spinner.dismiss();
        let previousContractPeriods: ContractPeriod[] = [];
        if (response !== null) {
          let contractPeriod = Object.values(response);
          let count = 1;
          contractPeriod.forEach((period) => {
            let periodStart = moment(period.start);
            let name = periodStart.format(this.dateUIFormat);
            if (period.end && period.end != "") {
              name =
                name + " - " + moment(period.end).format(this.dateUIFormat);
            }
            let newPeriod = {
              id: count,
              name: name,
              value: periodStart.format(this.dateUIFormat),
              effectiveDate: periodStart.format(this.dateFormat),
              expiredDate: period.end
                ? moment(period.end).format(this.dateFormat)
                : "",
            };

            previousContractPeriods.push(newPeriod);
            count++;
          });
        }
        this.contractPeriodsOptions = previousContractPeriods.sort(
          (a, b) =>
            new Date(b.effectiveDate).getTime() -
            new Date(a.effectiveDate).getTime()
        );
        this.loadOptions();
      });
  }

  getMostRecentContractPeriod(contractPeriods) {
    return contractPeriods.sort(
      (a, b) =>
        new Date(b.effectiveDate).getTime() -
        new Date(a.effectiveDate).getTime()
    )[0];
  }

  getPayerBillingOptionsByPeriod(benefitType, period) {
    if (this.currentPayer) {
      let spinnerText = "Loading Contract Period Rates";

      const spinner = this.loadingCtrl.create({
        content: spinnerText,
      });
      spinner.present();
      this.payersService
        .getPayerBillingOptions(this.currentPayer.payerId, benefitType, period)
        .pipe()
        .take(1)
        .subscribe(
          (response) => {
            spinner.dismiss();
            this.mapContractPeriodBillingOptions(response);
            this.showSection = true;
          },
          (error) => {
            spinner.dismiss();
            this.clearContractPeriodBillingOptions();
            this.showSection = false;
            this.showError(error);
          }
        );
    }
  }

  clearContractPeriodBillingOptions() {
    this.claimFormType = null;
    this.billBy = null;
  }

  mapContractPeriodBillingOptions(data) {
    if (data) {
      this.claimFormType = data.claimFormType;
      this.billBy = data.billGrouping;
      this.selectedBenefitType = data.benefitType;
    }
  }

  formatToTwoDecimals(num: number): string {
    return num.toFixed(2);
  }

  showError = (error) => {
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: error.message,
        });
      }
    }
  };

  getEffectiveDate() {
    return this.currentContractPeriod &&
      this.currentContractPeriod.effectiveDate
      ? this.formatDate(this.currentContractPeriod.effectiveDate)
      : "";
  }

  getExpiredDate() {
    return this.currentContractPeriod && this.currentContractPeriod.expiredDate
      ? this.formatDate(this.currentContractPeriod.expiredDate)
      : "";
  }

  formatDate(dateString: string): string {
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    const formattedMonth = (date.getMonth() + 1).toString().padStart(2, "0");
    const formattedDay = date.getDate().toString().padStart(2, "0");
    const formattedYear = date.getFullYear();

    return `${formattedMonth}/${formattedDay}/${formattedYear}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/payers/billing-options/billing-options.ts